<template>
	<ol
		:id="id"
		class="list-style-none d-flex justify-center flex-wrap amelipro-numbered-card__list"
		:class="{
			'items-per-line-4': itemsPerLine === 4,
			'items-per-line-3': itemsPerLine === 3,
			'items-per-line-2': itemsPerLine !== 4 && itemsPerLine !== 3,
		}"
	>
		<li
			v-for="(item, index) in items"
			:key="index"
			class="mb-4 numbered-card-item__wrapper"
		>
			<span
				aria-hidden="true"
				class="font-weight-regular number"
			>
				<span class="toto ap-blue--text text--darken-1">
					{{ index + 1 }}
				</span>
			</span>

			<div class="d-flex w-100 amelipro-card--numbered__wrapper">
				<div
					class="amelipro-card--numbered w-100"
					:style="generalCardStyles"
				>
					<div
						class="amelipro-card--numbered__content"
						:class="contentClasses"
					>
						<slot
							:name="`${id}-item-${index}`"
							v-bind="item"
						>
							<slot
								:name="`${id}-item`"
								v-bind="item"
							/>
						</slot>
					</div>
				</div>
			</div>
		</li>
	</ol>
</template>

<script setup lang="ts">
	import { PropType, computed } from 'vue';
	import { AmeliproNumberedCardItem } from './types';
	import { IndexedObject } from '../../types';
	import { convertToHex } from '../../functions/convertToHex';

	const props = defineProps({
		borderColor: {
			type: String,
			default: 'ap-grey lighten-2',
		},
		bordered: {
			type: Boolean,
			default: true,
		},
		cardColor: {
			type: String,
			default: 'ap-white',
		},
		contentClasses: {
			type: String,
			default: undefined,
		},
		id: {
			type: String,
			required: true,
		},
		items: {
			type: Array as PropType<AmeliproNumberedCardItem[]>,
			required: true,
		},
		itemsPerLine: {
			type: Number,
			default: 2,
		},
	});

	const generalCardStyles = computed<IndexedObject>(() => {
		const cardStyle: IndexedObject = { backgroundColor: `${convertToHex(props.cardColor)}` };

		if (props.bordered) {
			cardStyle.border = `1px solid ${convertToHex(props.borderColor)}`;
		}

		return cardStyle;
	});
</script>

<style lang="scss" scoped>

.amelipro-card--numbered__wrapper {
	padding-top: 1rem;
}
.amelipro-card--numbered {
	display: block;
	position: relative;
	padding: $card-padding--desktop;
	max-width: 100%;
	border-radius: $card-radius;
	text-decoration: none;
	overflow-wrap: break-word;
	white-space: normal;

	@media #{$media-down-md} {
		padding: $card-padding--tablet;
	}

	@media #{$media-down-sm} {
		padding: $card-padding--mobile;
	}
}

.amelipro-card--numbered__content {
	width: 100%;
	padding: $card-padding--desktop $card-padding--mobile;
	font-size: $font-size-xs;
	font-weight: $ap-font-weight-regular;
}

.numbered-card-item__wrapper {
	position: relative;

	& > div {
		min-height: 100%;
	}
}

.number {
	display: block;
	width: 100%;
	font-size: $font-size-sm;

	@media #{$media-down-sm} {
		font-size: $font-size-xs;
	}

	&::before {
		position: absolute;
		left: calc(50% - 1rem);
		top: 0;
		width: 2rem;
		height: 2rem;
		border-radius: 50%;
		border: 1px solid $ap-blue-darken1;
		background-color: $ap-white;
		content: "";
		z-index: 1;

		@media #{$media-down-sm} {
			left: calc(50% - 0.75rem);
			width: 1.5rem;
			height: 1.5rem;
			top: 0.25rem;
		}
	}

	& span {
		position: absolute;
		top: 0.25rem;
		width: 100%;
		font-weight: $ap-font-weight-semi-bold;
		text-align: center;
		z-index: 2;

		@media #{$media-down-sm} {
			top: 0.375rem;
		}
	}
}

.items-per-line-2 {

	& > li {

		width: calc(50% - 5px);
		min-width: calc(50% - 5px);
		max-width: calc(50% - 5px);

		&:nth-child(2n+2) {

			@media #{$media-up-md} {
				margin-left: 10px;
			}
		}
	}
}

.items-per-line-3 {

	& > li {

		@media #{$media-up-lg} {
			width: calc((100% / 3) - 10px);
			min-width: calc((100% / 3) - 10px);
			max-width: calc((100% / 3) - 10px);
		}

		@media #{$media-only-md} {
			width: calc(50% - 5px);
			min-width: calc(50% - 5px);
			max-width: calc(50% - 5px);
		}

		&:nth-child(2n+2) {

			@media #{$media-only-md} {
				margin-left: 10px;
			}
		}

		&:nth-child(3n+2),
		&:nth-child(3n+3) {

			@media #{$media-up-lg} {
				margin-left: 10px;
			}
		}
	}
}

.items-per-line-4 {

	& > li {

		@media #{$media-up-lg} {
			width: calc(25% - 9px);
			min-width: calc(25% - 9px);
			max-width: calc(25% - 9px);
		};

		@media #{$media-only-md} {
			width: calc(50% - 5px);
			min-width: calc(50% - 5px);
			max-width: calc(50% - 5px);
		}

		&:nth-child(2n+2) {

			@media #{$media-only-md} {
				margin-left: 10px;
			}
		}

		&:nth-child(4n+2),
		&:nth-child(4n+3),
		&:nth-child(4n+4) {

			@media #{$media-up-lg} {
				margin-left: 12px;
			}
		}
	}
}

.items-per-line-2,
.items-per-line-3,
.items-per-line-4 {

	& > li {

		@media #{$media-down-md} {
			width: 100%;
			min-width: 100%;
			max-width: 100%;
		}
	}
}
</style>
